import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

const PlayerEntry = () => {
    const [formData, setFormData] = useState({
        playerName: '',
        playerScore: '',
        // Additional fields for specific scoring types
        minutes: '',
        seconds: '',
        milliseconds: '',
        distance: '',
        unit: 'feet'
    });
    
    const [selectedGame, setSelectedGame] = useState(null);
    const [gameOptions, setGameOptions] = useState([]);
    const [message, setMessage] = useState("Select a game and input player data");
    const [messageType, setMessageType] = useState('info'); // 'info', 'success', 'error'
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Load available games on component mount
    useEffect(() => {
        const loadGames = async () => {
            try {
                const response = await axios.get('/games/current');
                setGameOptions(response.data);
            } catch (error) {
                console.error('Failed to load games:', error);
                setGameOptions([]);
                setMessage("Failed to load games. Please refresh the page.");
                setMessageType('error');
            }
        };
        loadGames();
    }, []);

    const handleGameSelect = (e) => {
        const shortName = e.target.value;
        if (!shortName) {
            setSelectedGame(null);
            setFormData({
                playerName: '',
                playerScore: '',
                minutes: '',
                seconds: '',
                milliseconds: '',
                distance: '',
                unit: 'feet'
            });
            setMessage("Select a game and input player data");
            setMessageType('info');
            return;
        }

        const game = gameOptions.find(g => g.shortName === shortName);
        setSelectedGame(game);
        
        // Reset form when game changes
        setFormData({
            playerName: '',
            playerScore: '',
            minutes: '',
            seconds: '',
            milliseconds: '',
            distance: '',
            unit: 'feet'
        });
        setMessage(`Selected: ${game.displayName}. Enter player data below.`);
        setMessageType('info');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Calculate final score based on scoring type
    const calculateFinalScore = () => {
        if (!selectedGame) return 0;

        switch (selectedGame.scoringType.toLowerCase()) {
            case 'time':
                // Convert time to total seconds for storage
                const minutes = parseFloat(formData.minutes) || 0;
                const seconds = parseFloat(formData.seconds) || 0;
                const milliseconds = parseFloat(formData.milliseconds) || 0;
                return (minutes * 60) + seconds + (milliseconds / 1000);
                
            case 'distance':
                // Convert distance to standard unit (feet)
                const distance = parseFloat(formData.distance) || 0;
                if (formData.unit === 'meters') {
                    return distance * 3.28084; // meters to feet
                }
                return distance;
                
            case 'rulerofthehill':
                // For Ruler of the Hill, we just need any positive value
                return 1;
                
            case 'points':
            case 'score':
            default:
                return parseFloat(formData.playerScore) || 0;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!selectedGame) {
            setMessage("Please select a game first.");
            setMessageType('error');
            return;
        }
        
        if (!formData.playerName.trim()) {
            setMessage("Please enter a player name.");
            setMessageType('error');
            return;
        }

        const finalScore = calculateFinalScore();
        
        // Special validation for Ruler of the Hill
        if (selectedGame.scoringType.toLowerCase() === 'rulerofthehill') {
            // Ruler of the Hill doesn't need score validation
        } else if (finalScore <= 0) {
            setMessage("Please enter a valid score.");
            setMessageType('error');
            return;
        }

        setIsSubmitting(true);
        
        try {
            const submissionData = {
                gameId: selectedGame.id,
                playerName: formData.playerName.trim(),
                playerScore: finalScore
            };

            const response = await axios.post('/scores', submissionData);
            const data = response.data;
            
            if (selectedGame.scoringType.toLowerCase() === 'rulerofthehill') {
                setMessage(`👑 ${formData.playerName} is now the Ruler of the Hill!`);
            } else if (data.isNew) {
                setMessage(`${formData.playerName} has been added to the scoreboard with a score of ${formatScoreForDisplay(finalScore)}`);
            } else {
                setMessage(`${formData.playerName}'s score has been updated to ${formatScoreForDisplay(finalScore)}`);
            }
            setMessageType('success');
            
            // Reset form
            setFormData({
                playerName: '',
                playerScore: '',
                minutes: '',
                seconds: '',
                milliseconds: '',
                distance: '',
                unit: 'feet'
            });
            
        } catch (error) {
            console.error('Error submitting score:', error);
            setMessage("Error submitting score. Please try again.");
            setMessageType('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Format score for display based on scoring type
    const formatScoreForDisplay = (score) => {
        if (!selectedGame) return score;
        
        switch (selectedGame.scoringType.toLowerCase()) {
            case 'time':
                const totalSeconds = parseFloat(score);
                const mins = Math.floor(totalSeconds / 60);
                const secs = Math.floor(totalSeconds % 60);
                const ms = Math.round((totalSeconds % 1) * 1000);
                return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
                
            case 'distance':
                return `${score.toFixed(2)} feet`;
                
            case 'rulerofthehill':
                return 'King 👑';
                
            case 'points':
            case 'score':
            default:
                return Math.round(score).toLocaleString();
        }
    };

    // Render appropriate input fields based on scoring type
    const renderScoreInput = () => {
        if (!selectedGame) return null;

        switch (selectedGame.scoringType.toLowerCase()) {
            case 'time':
                return (
                    <div className="margin-top-medium">
                        <label className="form-label">Time:</label>
                        <div className="time-input-container">
                            <input 
                                type="number" 
                                name="minutes" 
                                value={formData.minutes}
                                onChange={handleInputChange}
                                placeholder="Min"
                                min="0"
                                className="time-input"
                            />
                            <span>:</span>
                            <input 
                                type="number" 
                                name="seconds" 
                                value={formData.seconds}
                                onChange={handleInputChange}
                                placeholder="Sec"
                                min="0"
                                max="59"
                                className="time-input"
                            />
                            <span>.</span>
                            <input 
                                type="number" 
                                name="milliseconds" 
                                value={formData.milliseconds}
                                onChange={handleInputChange}
                                placeholder="MS"
                                min="0"
                                max="999"
                                className="time-input milliseconds"
                            />
                        </div>
                    </div>
                );

            case 'distance':
                return (
                    <div className="margin-top-medium">
                        <label className="form-label">Distance:</label>
                        <div className="distance-input-container">
                            <input 
                                type="number" 
                                name="distance" 
                                value={formData.distance}
                                onChange={handleInputChange}
                                placeholder="Distance"
                                min="0"
                                step="0.01"
                                className="distance-input"
                            />
                            <select 
                                name="unit" 
                                value={formData.unit} 
                                onChange={handleInputChange}
                                className="form-select"
                                style={{ width: 'auto' }}
                            >
                                <option value="feet">Feet</option>
                                <option value="meters">Meters</option>
                            </select>
                        </div>
                    </div>
                );

            case 'rulerofthehill':
                return (
                    <div className="margin-top-medium ruler-of-hill-info">
                        <p><strong>Ruler of the Hill Mode 👑</strong></p>
                        <p>Enter your name to claim the crown!</p>
                        <p style={{ fontSize: '14px', opacity: 0.8 }}>
                            Only the most recent player will be shown on the scoreboard
                        </p>
                    </div>
                );

            case 'points':
            case 'score':
            default:
                return (
                    <div className="margin-top-medium">
                        <label className="form-label" htmlFor="playerScore">Score:</label>
                        <input 
                            type="number" 
                            id="playerScore"
                            name="playerScore" 
                            value={formData.playerScore}
                            onChange={handleInputChange}
                            placeholder="Enter score"
                            min="0"
                            className="form-input"
                        />
                    </div>
                );
        }
    };

    const getMessageClass = () => {
        switch (messageType) {
            case 'error':
                return 'error-message';
            case 'success':
                return 'success-message';
            default:
                return '';
        }
    };

    return (
        <div className="fade-in">
            {/* Title Section */}
            <div className="title-style">
                <h1>Player Score Submission</h1>
            </div>
            
            {/* Main Form Section */}
            <div className="player-entry-form margin-top-medium">
                <form onSubmit={handleSubmit}>
                    <label className="form-label" htmlFor="gameSelect">Select a Game:</label>
                    <select 
                        id="gameSelect"
                        name="gameSelect" 
                        value={selectedGame?.shortName || ''} 
                        onChange={handleGameSelect}
                        className="form-select full-width"
                    >
                        <option value=''>--Select a Game--</option>
                        {gameOptions.map(game => (
                            <option key={game.shortName} value={game.shortName}>
                                {game.displayName}
                            </option>
                        ))}
                    </select>

                    {selectedGame && (
                        <>
                            <div className="game-info-preview margin-top-medium">
                                <strong>Game:</strong> {selectedGame.displayName}<br/>
                                <strong>Scoring Type:</strong> {selectedGame.scoringType.charAt(0).toUpperCase() + selectedGame.scoringType.slice(1)}
                            </div>

                            <div className="margin-top-medium">
                                <label className="form-label" htmlFor="playerName">Player Name:</label>
                                <input 
                                    type="text" 
                                    id="playerName"
                                    name="playerName" 
                                    value={formData.playerName} 
                                    onChange={handleInputChange} 
                                    placeholder="Enter player name"
                                    className="form-input full-width"
                                />
                            </div>

                            {renderScoreInput()}

                            <button 
                                type="submit" 
                                disabled={isSubmitting}
                                className={isSubmitting ? 'btn-loading full-width margin-top-medium' : 'btn-standard full-width margin-top-medium'}
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit Score'}
                            </button>
                        </>
                    )}
                </form>
            </div>
            
            {/* Message Display */}
            {message && (
                <div className={`margin-top-medium ${getMessageClass()}`}>
                    <p>{message}</p>
                </div>
            )}
        </div>
    );
};

export default PlayerEntry;
